import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { useState } from "react";

import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";

export type ActionItemColor =
  | "error"
  | "warning"
  | "success"
  | "primary"
  | "info";

export interface ActionItem<TRow> {
  label: string;
  icon?: React.ReactNode;
  color?: ActionItemColor;
  onClick?: (row: TRow) => void;
  link?: (row: TRow) => string;
}

interface ActionsCellProps<TRow> {
  row: TRow;
  items: ActionItem<TRow>[];
}

export const ActionsCell = <TRow,>({
  row,
  items,
}: ActionsCellProps<TRow>): React.ReactNode => {
  const navigate = useAppNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const getColor = (color: ActionItemColor) => theme.palette[color].main;

  return (
    <Box
      sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
    >
      <IconButton size="small" onClick={handleOpen} sx={{ color: "white" }}>
        <MoreVertIcon />
      </IconButton>
      {Boolean(anchorEl) && (
        <Menu
          anchorEl={anchorEl}
          open
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {items.map((item, index) => (
            <MenuItem
              // biome-ignore lint/suspicious/noArrayIndexKey: items are static
              key={index}
              onClick={() => {
                handleClose();
                if (item.link) {
                  navigate(item.link(row));
                } else {
                  item.onClick?.(row);
                }
              }}
              sx={item.color ? { color: getColor(item.color) } : undefined}
            >
              {item.icon && (
                <ListItemIcon
                  sx={item.color ? { color: "inherit" } : undefined}
                >
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
};
